import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Users, HelpCircle, X, Zap } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';

const Home = () => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    // Parallax effect
    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePosition({
                x: (e.clientX / window.innerWidth) * 20 - 10,
                y: (e.clientY / window.innerHeight) * 20 - 10
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <div className="bg-white">
            <SEO
                pageKey="home"
                defaultTitle="BPO Consultants | Free Global Staffing Strategy Session"
                defaultDescription="Unlock global talent and save 70% with our AI-augmented BPO strategy. Book your free consultation today."
                defaultSchema={JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "BPO Consultants Philippines",
                    "url": "https://bpoconsultants.ph",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": "https://bpoconsultants.ph/search?q={search_term_string}",
                        "query-input": "required name=search_term_string"
                    }
                }, null, 4)}
            />

            {/* Hero Section */}
            <section
                className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden transition-colors duration-300"
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="#A2CB52" />
                    </svg>
                </div>

                {/* Globe Video with Parallax & Interaction */}
                <div
                    className="absolute inset-0 z-0 opacity-80 pointer-events-none overflow-hidden"
                    style={{
                        transform: `translate(${mousePosition.x * -1}px, ${mousePosition.y * -1}px) scale(1.1)`,
                        transition: 'transform 0.1s ease-out'
                    }}
                >
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                    >
                        <source src="/assets/globe-design.mp4" type="video/mp4" />
                        {/* Fallback to GIF if video fails */}
                        <img src="/assets/globe-design.gif" alt="Globe Animation" className="w-full h-full object-cover" />
                    </video>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <FadeIn>
                            <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                                🚀 For Business Decision Makers Only
                            </span>
                            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight leading-tight drop-shadow-lg">
                                Smart Businesses <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A2CB52] to-[#B5D575]">
                                    Plan
                                </span> Before They Offshore.
                            </h1>
                            <p className="text-xl text-gray-100 mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                                Most BPO strategies fail because they buy "staff" instead of building a "strategy."
                                <br className="hidden md:block" />
                                <strong>We don't just fill seats. We build your global growth engine.</strong>
                            </p>

                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all duration-300 shadow-[0_0_20px_rgba(162,203,82,0.3)] hover:shadow-[0_0_30px_rgba(162,203,82,0.5)] transform hover:-translate-y-1 flex items-center justify-center relative z-20"
                                >
                                    Avail FREE Consultation
                                    <ArrowRight className="ml-2" size={20} />
                                </Link>
                                <Link
                                    to="/calculator"
                                    className="px-8 py-4 rounded-xl font-bold text-lg text-white border border-gray-600 hover:bg-white/5 transition-all flex items-center justify-center backdrop-blur-sm relative z-20"
                                >
                                    <TrendingUp className="mr-2 text-[#A2CB52]" size={20} />
                                    Calculate ROI First
                                </Link>
                            </div>
                            <p className="mt-6 text-sm text-gray-400">
                                No commitment. 100% Free Consultation.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* The "Missing Piece" / Problem Section */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <FadeIn>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-6">
                                Why 90% of Offshoring Attempts <span className="text-red-500">Fail</span> to Scale
                            </h2>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                You’ve likely heard the horror stories: ghosting employees, poor communication, and work that needs to be redone.
                            </p>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                The problem isn't the <em>people</em>. The problem is the <strong>model</strong>. Traditional agencies treat humans like hardware—plug and play. Real success requires a <strong>strategic partnership</strong> that integrates culture, AI, and workflow.
                            </p>

                            <div className="bg-white p-6 rounded-xl border-l-4 border-[#A2CB52] shadow-sm">
                                <h4 className="font-bold text-[#20222D] mb-2 flex items-center">
                                    <HelpCircle className="text-[#A2CB52] mr-2" size={20} />
                                    The 3 Pillars of BPO Success
                                </h4>
                                <ul className="space-y-4 mt-4">
                                    <li className="flex items-center text-gray-700">
                                        <CheckCircle size={18} className="text-[#A2CB52] mr-2 flex-shrink-0" />
                                        1. Elite Talent Acquisition (Top 1%)
                                    </li>
                                    <li className="flex items-center text-gray-700 font-bold bg-yellow-50 p-2 rounded-lg -ml-2">
                                        <HelpCircle size={18} className="text-yellow-600 mr-2 flex-shrink-0" />
                                        2. [HIDDEN] - The Secret to Retention
                                    </li>
                                    <li className="flex items-center text-gray-700">
                                        <CheckCircle size={18} className="text-[#A2CB52] mr-2 flex-shrink-0" />
                                        3. AI-Augmented Workflow
                                    </li>
                                </ul>
                                <p className="text-sm text-gray-500 mt-4 italic">
                                    *We reveal Pillar #2 and how to implement it during your free consultation.*
                                </p>
                            </div>
                        </FadeIn>
                        <FadeIn delay={0.2}>
                            <div className="relative">
                                <div className="absolute inset-0 bg-[#A2CB52] transform rotate-3 rounded-2xl opacity-20"></div>
                                <img
                                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800"
                                    alt="Team collaboration failure vs success"
                                    className="relative rounded-2xl shadow-xl w-full"
                                />
                                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg border border-gray-100 max-w-xs z-20">
                                    <div className="flex items-start">
                                        <div className="bg-red-100 p-2 rounded-lg mr-4">
                                            <X className="text-red-500" size={24} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-[#20222D]">Legacy BPO</p>
                                            <p className="text-sm text-gray-500">High turnover, low ownership</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute -top-6 -right-6 bg-white p-6 rounded-xl shadow-lg border border-gray-100 max-w-xs z-20">
                                    <div className="flex items-start">
                                        <div className="bg-[#A2CB52]/20 p-2 rounded-lg mr-4">
                                            <CheckCircle className="text-[#A2CB52]" size={24} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-[#20222D]">Our Model</p>
                                            <p className="text-sm text-gray-500">Integrated & AI-Enabled</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* The "Incomplete Truth" Comparison */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <FadeIn>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-4">
                                What You Know vs. What You Don't
                            </h2>
                            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                                Saving money is just the tip of the iceberg. The real value lies in what you haven't unlocked yet.
                            </p>
                        </FadeIn>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Visible / Known */}
                        <FadeIn className="bg-gray-50 p-8 rounded-2xl border border-gray-100 h-full">
                            <h3 className="text-2xl font-bold text-gray-400 mb-6 flex items-center">
                                <span className="bg-gray-200 text-gray-600 w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3">A</span>
                                The "Standard" View
                            </h3>
                            <ul className="space-y-4">
                                {[
                                    "Labor Cost Savings (~50%)",
                                    "24/7 Operations",
                                    "Access to English Speakers",
                                    "Basic Task Delegation"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center text-gray-500">
                                        <CheckCircle size={20} className="text-gray-300 mr-3 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </FadeIn>

                        {/* Hidden / Specialist */}
                        <FadeIn delay={0.2} className="bg-[#20222D] p-8 rounded-2xl text-white relative overflow-hidden shadow-2xl h-full flex flex-col">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#A2CB52] blur-[80px] opacity-20 pointer-events-none"></div>
                            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                                <span className="bg-[#A2CB52] text-[#20222D] w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3">B</span>
                                The BPO Consultants Edge
                            </h3>
                            <ul className="space-y-4 relative z-10 flex-grow">
                                {[
                                    "70%+ Cost Reduction (Optimized)",
                                    "AI-Augmented Productivity (2x Output)",
                                    "Cultural & Operational Integration",
                                    "Retention-First Architecture",
                                    "Global Talent, Local Mindset"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center text-white font-medium">
                                        <CheckCircle size={20} className="text-[#A2CB52] mr-3 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 pt-8 border-t border-gray-700">
                                <p className="text-[#A2CB52] font-bold mb-2">Want to unlock Column B?</p>
                                <Link to="/contact" className="text-white underline hover:text-[#A2CB52] transition-colors">
                                    Let's discuss your custom strategy &rarr;
                                </Link>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Process / Offer Section */}
            <section className="py-24 bg-[#A2CB52]/10 relative overflow-hidden">
                {/* Decorative background pattern */}
                <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
                        </pattern>
                        <rect width="100" height="100" fill="url(#grid)" />
                    </svg>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-16">
                        <FadeIn>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#20222D] mb-4">
                                Your Path to Global Expansion
                            </h2>
                            <p className="text-xl text-gray-600">
                                It starts with a conversation, not a contract.
                            </p>
                        </FadeIn>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <HelpCircle size={40} />,
                                title: "1. Free Discovery",
                                desc: "We analyze your current workflow, identify bottlenecks, and calculate your exact potential savings.",
                                free: true
                            },
                            {
                                icon: <Zap size={40} />,
                                title: "2. Strategic Design",
                                desc: "We build a custom org chart and tech stack (including AI tools) tailored to your industry.",
                                free: false
                            },
                            {
                                icon: <Users size={40} />,
                                title: "3. Execution",
                                desc: "We recruit, vet, and onboard your elite team, ensuring day-1 cultural alignment.",
                                free: false
                            }
                        ].map((step, i) => (
                            <FadeIn key={i} delay={i * 0.1} className={`p-8 rounded-2xl border transition-all duration-300 h-full flex flex-col ${step.free ? 'bg-white border-[#A2CB52] shadow-xl md:scale-105 ring-4 ring-[#A2CB52]/20 relative z-20' : 'bg-white/50 border-gray-100 border-dashed hover:bg-white hover:border-[#A2CB52]/30 hover:shadow-lg'}`}>
                                <div className={`mb-6 ${step.free ? 'text-[#A2CB52]' : 'text-gray-400'}`}>
                                    {step.icon}
                                </div>
                                <h3 className="text-xl font-bold text-[#20222D] mb-3 flex items-center">
                                    {step.title}
                                    {step.free && <span className="ml-2 bg-[#A2CB52] text-[#20222D] text-xs px-2 py-1 rounded-full uppercase tracking-wider font-bold">Free</span>}
                                </h3>
                                <p className="text-gray-600 flex-grow">
                                    {step.desc}
                                </p>
                            </FadeIn>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <FadeIn delay={0.4}>
                            <Link
                                to="/contact"
                                className="bg-[#20222D] text-white px-10 py-5 rounded-2xl font-bold text-xl hover:bg-gray-800 transition-all duration-300 shadow-xl inline-flex items-center transform hover:scale-105"
                            >
                                Start Step 1: Avail FREE Consultation
                                <ArrowRight className="ml-2" size={24} />
                            </Link>
                            <p className="mt-4 text-gray-500 text-sm">
                                Limited slots available for free strategy sessions this week.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
